import React, { useRef, useEffect, useState, useCallback } from 'react';
import { config } from '../../config/config';
import { validateAddress, type AddressValidationResult } from '../../services/addressValidationService';
import { extractCityFromComponents } from '../../utils/googleAddressComponents';

export interface AddressData {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
  /** ISO-3166-1 alpha-2 country code extracted from Google Places (e.g. 'US', 'CA'). */
  country?: string;
  coordinates?: { lat: number; lng: number };
  validation?: AddressValidationResult;
  missingStreetNumber?: boolean; // True if address was selected without a street number
  placeName?: string; // Business/establishment name if selected (e.g., "Home Depot", "Dr. Smith's Office")
  isEstablishment?: boolean; // True if selected place is a business/establishment
}

interface GooglePlacesAddressProps {
  value: string;
  onChange: (addressData: AddressData) => void;
  onInputChange?: (value: string) => void;
  onValidationComplete?: (result: AddressValidationResult) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  error?: string;
  searchType?: 'address' | 'establishment' | 'geocode' | 'all'; // Control search type - 'all' removes type restriction
  enableValidation?: boolean; // Enable Google Address Validation API
  showValidationStatus?: boolean; // Show validation indicator inline
}

const GooglePlacesAddress: React.FC<GooglePlacesAddressProps> = ({
  value,
  onChange,
  onInputChange,
  onValidationComplete,
  placeholder = 'Start typing your address...',
  required = false,
  disabled = false,
  className = '',
  error,
  searchType = 'address',
  enableValidation = true,
  showValidationStatus = false
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const onChangeRef = useRef(onChange);
  const onValidationCompleteRef = useRef(onValidationComplete);
  const isInitializedRef = useRef(false);
  
  // Validation state
  const [validationState, setValidationState] = useState<{
    status: 'idle' | 'validating' | 'validated' | 'fallback';
    confidence?: 'high' | 'medium' | 'low' | 'unknown';
    message?: string;
  }>({ status: 'idle' });

  // Keep refs updated
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  
  useEffect(() => {
    onValidationCompleteRef.current = onValidationComplete;
  }, [onValidationComplete]);
  
  // Validate address after selection
  const performValidation = useCallback(async (addressData: Omit<AddressData, 'validation'>) => {
    if (!enableValidation) return;

    setValidationState({ status: 'validating' });

    try {
      const result = await validateAddress({
        addressLine1: addressData.address1,
        addressLine2: addressData.address2,
        city: addressData.city,
        state: addressData.state,
        zipCode: addressData.zipCode,
        country: addressData.country
      });
      
      setValidationState({
        status: result.validationStatus === 'fallback' ? 'fallback' : 'validated',
        confidence: result.confidence,
        message: result.message
      });
      
      // Notify parent of validation result
      onValidationCompleteRef.current?.(result);
      
      // Update address with validation data and corrected address if available
      if (result.correctedAddress && result.confidence === 'high') {
        onChangeRef.current({
          ...addressData,
          address1: result.correctedAddress.addressLine1,
          city: result.correctedAddress.city,
          state: result.correctedAddress.state,
          zipCode: result.correctedAddress.zipCode,
          coordinates: result.coordinates || addressData.coordinates,
          validation: result
        });
      } else {
        onChangeRef.current({
          ...addressData,
          coordinates: result.coordinates || addressData.coordinates,
          validation: result
        });
      }
    } catch (error) {
      console.error('Address validation error:', error);
      const fallbackResult: AddressValidationResult = {
        isValid: true,
        validationStatus: 'fallback',
        confidence: 'unknown',
        message: 'Validation unavailable'
      };
      setValidationState({ 
        status: 'fallback',
        message: 'Validation unavailable'
      });
      // Always notify parent so isValidating can be set to false
      onValidationCompleteRef.current?.(fallbackResult);
    }
  }, [enableValidation]);

  useEffect(() => {
    // Prevent re-initialization
    if (isInitializedRef.current) {
      return;
    }

    const initAutocomplete = () => {
      if (!inputRef.current || !window.google?.maps?.places?.Autocomplete) {
        console.warn('Google Places Autocomplete not available yet');
        return;
      }

      if (autocompleteRef.current) {
        // Already initialized
        return;
      }

      try {
        // Create autocomplete instance with configurable search type
        // 'address' = street addresses only
        // 'establishment' = businesses, restaurants, stores, etc.
        // 'geocode' = all geocodeable locations
        // 'all' = no type restriction, returns addresses AND businesses
        const autocompleteOptions: google.maps.places.AutocompleteOptions = {
          componentRestrictions: { country: ['us', 'ca'] },
          fields: ['address_components', 'formatted_address', 'geometry', 'name', 'types']
        };
        
        // Only add type restriction if not 'all'
        if (searchType !== 'all') {
          autocompleteOptions.types = [searchType];
        }
        
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          inputRef.current,
          autocompleteOptions
        );

        // Listen for place selection
        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current?.getPlace();

          // Check for geometry to ensure user selected from dropdown
          if (!place || !place.geometry || !place.address_components) {
            console.warn('No valid place selected from dropdown');
            return;
          }

          let streetNumber = '';
          let route = '';
          let subpremise = '';
          let state = '';
          let zipCode = '';
          let countryCode = '';

          // Extract address components with enhanced parsing
          place.address_components.forEach((component) => {
            const types = component.types;

            // Street number
            if (types.includes('street_number')) {
              streetNumber = component.long_name;
            }

            // Route/Street name
            if (types.includes('route')) {
              route = component.long_name;
            }

            // Subpremise (suite, apartment, unit, floor, etc.)
            if (types.includes('subpremise')) {
              subpremise = component.long_name;
            }

            // State
            if (types.includes('administrative_area_level_1')) {
              state = component.short_name;
            }

            // Country (ISO-3166-1 alpha-2 short name: 'US', 'CA', etc.)
            if (types.includes('country')) {
              countryCode = component.short_name;
            }

            // ZIP Code - check multiple types
            if (!zipCode && types.includes('postal_code')) {
              zipCode = component.long_name;
            } else if (!zipCode && types.includes('postal_code_suffix')) {
              zipCode = component.long_name;
            } else if (!zipCode && types.includes('postal_code_prefix')) {
              zipCode = component.long_name;
            }
          });
          
          // City - priority-ordered fallback chain (handles townships, sublocalities, etc.)
          const city = extractCityFromComponents(
            place.address_components.map((component) => ({
              types: component.types,
              name: component.long_name,
            }))
          );

          // Fallback: extract postal code from formatted address if not found in components.
          // This handles cases like road selections without street numbers.
          // Matches a US ZIP (5 or 5+4) or a Canadian postal code (A1A 1A1, separator optional).
          if (!zipCode && place.formatted_address) {
            const zipMatch = place.formatted_address.match(
              /\b(\d{5}(?:-\d{4})?|[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)\b/
            );
            if (zipMatch) {
              zipCode = zipMatch[1];
              console.log('Google Places: postal code extracted from formatted address:', zipCode);
            }
          }

          // Detect if this is an establishment (business, store, etc.)
          const placeTypes = (place as any).types || [];
          const isEstablishment = placeTypes.some((type: string) => 
            ['establishment', 'store', 'restaurant', 'food', 'point_of_interest', 
             'health', 'doctor', 'hospital', 'pharmacy', 'bank', 'atm',
             'shopping_mall', 'supermarket', 'grocery_or_supermarket'].includes(type)
          );
          
          // Extract place name for establishments
          const placeName = (isEstablishment || searchType === 'establishment') && place.name ? place.name : undefined;

          // Build address1 - handle both addresses and establishments
          let address1 = '';
          
          // For establishments, use the street address (not the business name)
          if (streetNumber && route) {
            address1 = `${streetNumber} ${route}`;
          } else if (route) {
            address1 = route;
          } else if (isEstablishment && place.formatted_address) {
            // For establishments without clear street, extract address from formatted
            const addressParts = place.formatted_address.split(',');
            // First part is usually the business name, second is the street
            if (addressParts.length > 1) {
              address1 = addressParts[1].trim();
            } else if (addressParts.length > 0) {
              address1 = addressParts[0].trim();
            }
          } else if (place.name && !isEstablishment) {
            // If no street but there's a name (could be a landmark), use it
            address1 = place.name;
          } else if (place.formatted_address) {
            // Fallback: extract the first part of formatted address
            const addressParts = place.formatted_address.split(',');
            if (addressParts.length > 0) {
              address1 = addressParts[0].trim();
            }
          }
          address1 = address1.trim();

          // Add debug logging to help troubleshoot parsing issues
          console.log('Google Places address parsing:', {
            formattedAddress: place.formatted_address,
            components: place.address_components.map(c => ({
              types: c.types,
              long: c.long_name,
              short: c.short_name
            })),
            parsed: { 
              address1, 
              address2: subpremise,
              city, 
              state, 
              zipCode,
              streetNumber,
              route,
              subpremise
            }
          });

          // Warn if critical components are missing
          if (!city) {
            console.warn('Google Places: No city found in address components');
          }
          if (!state) {
            console.warn('Google Places: No state found in address components');
          }
          if (!zipCode) {
            console.warn('Google Places: No ZIP code found in address components');
          }
          
          // Check if street number is missing (important for service addresses)
          const missingStreetNumber = !streetNumber && route && searchType === 'address';
          if (missingStreetNumber) {
            console.warn('Google Places: Street number missing - user should provide one');
          }

          // Extract coordinates if available
          const coordinates = place.geometry?.location ? {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          } : undefined;

          const addressData: Omit<AddressData, 'validation'> = {
            address1,
            address2: subpremise || undefined,
            city,
            state,
            zipCode,
            country: countryCode || undefined,
            coordinates,
            missingStreetNumber,
            placeName,
            isEstablishment
          };
          
          // Call onChange immediately with Places data
          onChangeRef.current?.(addressData);
          
          // Then perform validation (async, non-blocking)
          performValidation(addressData);
        });

        console.log('Google Places Autocomplete initialized successfully');
        isInitializedRef.current = true;
      } catch (error) {
        console.error('Error initializing Google Places:', error);
      }
    };

    // Check if Google Maps API is already available
    if (window.google?.maps?.places?.Autocomplete) {
      console.log('Google Maps already loaded, initializing autocomplete');
      // Use setTimeout to ensure the input ref is ready
      setTimeout(() => initAutocomplete(), 50);
      return;
    }

    // Check if script is already in the DOM
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`) as HTMLScriptElement;
    
    if (existingScript) {
      // Script exists, wait for it to load
      console.log('Google Maps script tag found, waiting for load...');
      
      let attempts = 0;
      const maxAttempts = 100; // 10 seconds total (100ms * 100 = 10s)
      
      const checkGoogleLoaded = setInterval(() => {
        attempts++;
        
        // Check multiple conditions to ensure Places API is fully ready
        const isGoogleReady = window.google?.maps?.places?.Autocomplete;
        const isPlacesServiceReady = window.google?.maps?.places?.PlacesService;
        
        if (isGoogleReady && isPlacesServiceReady) {
          console.log('Google Maps loaded via existing script (attempt ' + attempts + ')');
          clearInterval(checkGoogleLoaded);
          setTimeout(() => initAutocomplete(), 50);
        } else if (window.google?.maps && !window.google?.maps?.places && attempts > 20) {
          // Google Maps loaded but without Places library
          clearInterval(checkGoogleLoaded);
          console.error('Google Maps loaded but Places library is missing!');
          console.error('The script that loaded Google Maps must include libraries=places');
          console.error('Current script:', existingScript?.src || 'Unknown');
        } else if (attempts >= maxAttempts) {
          clearInterval(checkGoogleLoaded);
          console.error('Google Maps failed to load after 10 seconds. Status:', {
            google: !!window.google,
            maps: !!window.google?.maps,
            places: !!window.google?.maps?.places,
            Autocomplete: !!window.google?.maps?.places?.Autocomplete,
            PlacesService: !!window.google?.maps?.places?.PlacesService,
            script: existingScript?.src || 'No script found'
          });
          console.error('Make sure the Google Maps script includes the "places" library');
        }
      }, 100);

      return () => {
        clearInterval(checkGoogleLoaded);
        if (autocompleteRef.current) {
          try {
            google.maps.event.clearInstanceListeners(autocompleteRef.current);
          } catch (e) {
            // Ignore cleanup errors
          }
        }
      };
    }

    // No script exists, create and load it
    console.log('Loading Google Maps script...');
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${config.googleMapsApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('Google Maps script loaded successfully');
      setTimeout(initAutocomplete, 100);
    };
    script.onerror = (error) => {
      console.error('Error loading Google Maps script:', error);
    };
    document.head.appendChild(script);

    // Cleanup
    return () => {
      if (autocompleteRef.current) {
        try {
          google.maps.event.clearInstanceListeners(autocompleteRef.current);
          autocompleteRef.current = null;
        } catch (e) {
          // Ignore cleanup errors
        }
      }
      isInitializedRef.current = false;
    };
  }, []); // Empty dependency array - initialize only once

  // Reset validation state when input changes manually
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValidationState({ status: 'idle' });
    onInputChange?.(e.target.value);
  };

  return (
    <>
      <style>{`
        /* Style Google Places Autocomplete dropdown suggestions */
        .pac-container {
          background-color: white !important;
          border-radius: 0.75rem !important;
          border: 1px solid rgb(209, 213, 219) !important;
          margin-top: 4px !important;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
          font-family: inherit !important;
          z-index: 9999 !important;
        }

        .pac-item {
          background-color: white !important;
          padding: 0.75rem 1rem !important;
          border-top: 1px solid rgb(243, 244, 246) !important;
          cursor: pointer !important;
          font-size: 0.875rem !important;
          line-height: 1.25rem !important;
          color: rgb(17, 24, 39) !important;
        }

        .pac-item:first-child {
          border-top: none !important;
        }

        .pac-item:hover {
          background-color: rgb(243, 244, 246) !important;
        }

        .pac-item-selected {
          background-color: rgb(237, 233, 254) !important;
        }

        .pac-icon {
          margin-top: 6px !important;
        }

        .pac-item-query {
          color: rgb(17, 24, 39) !important;
          font-size: 0.875rem !important;
        }

        .pac-matched {
          color: rgb(17, 24, 39) !important;
          font-weight: 600 !important;
        }
        
        @keyframes validation-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div className="relative">
        <input
          ref={inputRef}
          type="search"
          name="address1"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={className}
        />
        
        {/* Inline validation status indicator */}
        {showValidationStatus && validationState.status !== 'idle' && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {validationState.status === 'validating' && (
              <div 
                className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full"
                style={{ animation: 'validation-spin 0.8s linear infinite' }}
                title="Validating address..."
              />
            )}
            {validationState.status === 'validated' && validationState.confidence === 'high' && (
              <div 
                className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center"
                title="Address verified"
              >
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
            {validationState.status === 'validated' && validationState.confidence !== 'high' && (
              <div 
                className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center"
                title={validationState.message || "Address partially verified"}
              >
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
            {validationState.status === 'fallback' && (
              <div 
                className="w-5 h-5 bg-ink-400 rounded-full flex items-center justify-center"
                title="Could not verify - you can still proceed"
              >
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            )}
          </div>
        )}
        
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
        
        {/* Validation message */}
        {showValidationStatus && validationState.message && validationState.status !== 'validating' && (
          <p className={`mt-1 text-xs ${
            validationState.status === 'validated' && validationState.confidence === 'high'
              ? 'text-emerald-600'
              : validationState.status === 'fallback'
                ? 'text-ink-500'
                : 'text-amber-600'
          }`}>
            {validationState.message}
          </p>
        )}
      </div>
    </>
  );
};

export default GooglePlacesAddress;

